import * as React from 'react';
import english from '../i18n/en.json';

const STORAGE_KEY = 'bcharg-language';
const isLanguage = value => value === 'es' || value === 'en';
const translate = (language, text) => language === 'en' ? (english[text] || text) : text;
const spanish = Object.fromEntries(Object.entries(english).map(([es, en]) => [en, es]));

// Production Gatsby copies Head into the document on initial render only.
// Keep those published nodes in sync when the visitor switches languages.
const updateMetadata = language => {
  const title = spanish[document.title] || document.title;
  document.title = translate(language, title);
  document.querySelectorAll('meta[name="description"], meta[property="og:title"], meta[property="og:description"], meta[name="twitter:title"], meta[name="twitter:description"]').forEach(meta => {
    const source = spanish[meta.content] || meta.content;
    meta.content = translate(language, source);
  });
  const locale = document.querySelector('meta[property="og:locale"]');
  if (locale) locale.content = language === 'es' ? 'es_AR' : 'en_US';
};

// Gatsby renders page bodies and Head in separate roots. A shared store keeps
// their language synchronized, with a Spanish server snapshot for hydration.
let currentLanguage = 'es';
const listeners = new Set();
const subscribe = listener => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
const getSnapshot = () => currentLanguage;
const getServerSnapshot = () => 'es';

const selectLanguage = value => {
  if (!isLanguage(value)) return;
  currentLanguage = value;
  document.documentElement.lang = value;
  updateMetadata(value);
  try { window.localStorage.setItem(STORAGE_KEY, value); } catch {}
  // A stale explicit query must not override the choice after a reload.
  const url = new URL(window.location.href);
  if (url.searchParams.has('lang')) {
    url.searchParams.set('lang', value);
    window.history.replaceState(window.history.state, '', url);
  }
  listeners.forEach(listener => listener());
};

export const LanguageProvider = ({ children }) => {
  React.useEffect(() => {
    const requested = new URLSearchParams(window.location.search).get('lang');
    let saved;
    try {
      saved = window.localStorage.getItem(STORAGE_KEY) || window.localStorage.getItem('bcharg-token-language');
    } catch { /* The selector also works with browser storage disabled. */ }
    selectLanguage(isLanguage(requested) ? requested : isLanguage(saved) ? saved : 'es');
  }, []);
  return children;
};

export const useLanguage = () => {
  const language = React.useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  return React.useMemo(() => ({
    language, selectLanguage, t: text => translate(language, text),
  }), [language]);
};
